import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { camBasis, listWorldObjects, createWorldObject, updateWorldObject, deleteWorldObject } from '../services/worldObjects';
import { listWorldAssets, registerWorldAsset } from '../services/worldAssets';
import { ROOMS, TOWN_LOCATIONS } from '../rooms';
import { derivedPortals, DOOR_TRIGGER } from '../city/venueRegistry';
import { adminPanel, AdminClose } from './adminChrome';

// PortalPanel — place a door, in the world, without a deploy (?portal=edit).
//
// The asset is registered from here too, which closes the loop: a model dropped
// into public/models becomes a registry row, and the registry row becomes as many
// placed entrances as the city needs, all sharing one asset_id so they can
// instance. No SQL, no curl, no build.
const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;
const panel = adminPanel({
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 42,
  width: 212, pointerEvents: 'auto', color: '#d9ecff', font: '12.5px system-ui, sans-serif',
  background: 'rgba(8,14,22,0.88)', border: '1px solid rgba(125,211,255,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  maxHeight: '70vh', overflowY: 'auto',
});
const btn = {
  pointerEvents: 'auto', background: 'rgba(125,211,255,0.14)', color: '#7dd3ff',
  border: '1px solid rgba(125,211,255,0.4)', borderRadius: 8, padding: '8px 0', cursor: 'pointer',
  font: '600 13px system-ui', textAlign: 'center', userSelect: 'none',
};
const lab = { fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase', opacity: 0.7, margin: '9px 0 4px' };

// The one asset we ship today. Registering is idempotent (the API upserts on
// slug), so the button is safe to press twice.
const SEED = {
  slug: 'subway_entrance',
  name: 'Subway Entrance',
  kind: 'mesh',
  source: 'imported',
  originRef: 'DOCS/WORLD/models/subway_entrance__street_assets_vol._01.glb',
  variants: [{ tier: 'lod0', url: '/models/venues/subway_entrance.glb', tris: 6232 }],
  budget: { tris: 6232 },
  // Measured with scripts/mesh_bounds.mjs after --strip-material=Ground:
  // 5.8 x 8.5 x 14.7 metres, five of them below the pavement.
  bounds: { min: [-2.3, -4.96, -10.0], max: [3.5, 3.55, 4.7] },
};

const r2 = (v) => Math.round(v * 100) / 100;

// ── HOLD SHIFT FOR FINE ──────────────────────────────────────────────────────
// The coarse steps are right for finding a door and wrong for fitting one. A
// trigger starts 2 units deep, and 2 → 1.5 → 1 → 0.5 → 0 never passes through
// the 0.3-or-so that a doorway actually wants — so the only reachable settings
// were "swallows the pavement" and "gone".
//
// The modifier is read off each CLICK EVENT rather than from tracked key state,
// because tracked state is what gets stuck down: lose window focus mid-drag,
// alt-tab, or hit a keyup the panel never sees, and every subsequent press is
// silently fine (or silently coarse) with nothing on screen to explain it. The
// event cannot disagree with the keyboard. Tracked state is used ONLY to label
// the buttons, where being briefly wrong costs nothing.
const STEP = { move: 1, trig: 0.5, size: 0.5, pit: 0.5, rot: Math.PI / 8 };
const FINE = { move: 0.1, trig: 0.05, size: 0.05, pit: 0.1, rot: Math.PI / 64 };
const stepFor = (kind, e) => (e?.shiftKey ? FINE : STEP)[kind];

// A box with a zero or negative extent can never contain a point, so the door
// stops working while still being drawn and still being listed. There is no
// legitimate reason to want one, and no way to tell it apart from a broken
// trigger from inside the world.
const MIN_EXTENT = 0.1;
const EXTENT_KEYS = new Set(['w', 'h', 'd']);

export default function PortalPanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'portal';
  const interior = useWorldStore((s) => s.interior);
  const sel = useWorldStore((s) => s.portalSel);
  const setSel = useWorldStore((s) => s.setPortalSel);
  const bump = useWorldStore((s) => s.bumpPortals);
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  // WHAT THE NEXT DOOR WILL BE, chosen before placing rather than corrected
  // after. This used to be hardcoded to the subway, which made the tool a
  // subway-entrance placer wearing the name of a general one: placing a door at
  // the concert hall dropped a 6.7-unit stairwell in front of it, pointed at the
  // wrong room, and both had to be undone by hand.
  const [newRef, setNewRef] = useState('');
  // '' means NO MODEL — a trigger volume and nothing else. That is the common
  // case, not the exotic one: most venues already have a door modelled into
  // their facade, and what they are missing is only the volume in front of it.
  // The subway is the exception, because its entrance IS the asset.
  const [newModel, setNewModel] = useState('');
  // LABELS ONLY — never the action. See the note on STEP/FINE above.
  const [fineLabel, setFineLabel] = useState(false);
  // The town's own venues' doors (city/venueRegistry derivedPortals): drawn and
  // fired by Portals, but saved nowhere — so they never appeared in the list
  // above, and the Blackroom's could not be touched (Khari, 2026-11-01: "the depth
  // of the portal for the blackroom is too large, but I am unable to edit it").
  const registry = useWorldStore((s) => s.venueRegistry);
  const anchorOverrides = useWorldStore((s) => s.venueAnchorOverrides);

  // Every zone, so the target picker can offer the station at the far end while
  // you are standing on a platform underground.
  const reload = () => {
    listWorldObjects(null, 'portal').then((r) => setRows(r || []));
    listWorldAssets().then((a) => setAssets(a || []));
  };
  useEffect(() => { if (ON) reload(); }, [ON]);

  // `e.shiftKey` on the keyboard events rather than tracking down/up separately:
  // it reports the CURRENT modifier state on every key event, so a keyup for any
  // other key also corrects a Shift the panel missed. `blur` covers alt-tab,
  // which is the way a held key normally gets stranded.
  useEffect(() => {
    if (!ON) return undefined;
    const sync = (e) => setFineLabel(!!e.shiftKey);
    const off = () => setFineLabel(false);
    window.addEventListener('keydown', sync);
    window.addEventListener('keyup', sync);
    window.addEventListener('blur', off);
    return () => {
      window.removeEventListener('keydown', sync);
      window.removeEventListener('keyup', sync);
      window.removeEventListener('blur', off);
    };
  }, [ON]);

  // WHICH VENUES HAVE NO DOOR. The same question Portals warns about, asked here
  // where it can be acted on — and the reason the room picker defaults to one of
  // them rather than to whatever happens to be first alphabetically. Since doors
  // became the only way in, a venue on this list is a venue nobody can enter,
  // which makes it the overwhelmingly likely reason the panel is open at all.
  const doored = new Set(rows.filter((o) => o.zone_id === 'street').map((o) => o.data?.target?.ref || o.ref).filter(Boolean));
  const doorless = TOWN_LOCATIONS.filter((l) => l.interiorId && !doored.has(l.interiorId));

  useEffect(() => {
    if (!ON || newRef) return;
    setNewRef(doorless[0]?.interiorId || TOWN_LOCATIONS.find((l) => l.interiorId)?.interiorId || '');
    // Only seeds the empty selection — re-picking it on every reload would fight
    // the choice as soon as placing a door removes that venue from the list.
  }, [ON, doorless.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ON) return null;

  const cur = rows.find((o) => o.object_id === sel) || null;
  const asset = assets.find((a) => a.slug === SEED.slug) || null;
  const target = cur?.data?.target || { kind: 'room', ref: cur?.ref || '' };

  const say = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { say(e.message); } finally { setBusy(false); }
  };

  // CUSTOMISE AN AUTOMATIC DOOR: saved as an ordinary door at exactly its pose
  // and trigger, which every control below can then edit. A saved door for a
  // venue replaces its automatic one (derivedPortals skips it), so nothing moves
  // on screen until you change something — and deleting it brings the automatic
  // one back.
  const auto = interior ? [] : derivedPortals(registry, anchorOverrides, rows);
  const customise = (d) => run(async () => {
    const saved = await createWorldObject({
      zone_id: 'street', kind: 'portal', ref: d.ref,
      pos: [r2(d.pos[0]), 0, r2(d.pos[2])], rotY: r2(d.rotY), size: 1,
      data: { trigger: { ...DOOR_TRIGGER }, target: { kind: 'room', ref: d.ref } },
    });
    setSel(saved?.object_id ?? null); reload(); bump();
    say(`${ROOMS[d.ref]?.name || d.ref}: its door is yours to edit now`);
  });

  const register = () => run(async () => {
    await registerWorldAsset(SEED);
    reload(); say('asset registered');
  });

  // Placed AHEAD of you, like the resident tool — you stand where the entrance
  // should be and press the button, rather than typing coordinates for a place
  // you are already standing in.
  // Placed in the zone you are STANDING in — 'street' outside, the room id
  // inside. An exit portal belongs on the platform, and it would be perverse to
  // have to go outside to create the thing that brings you back out.
  const place = () => run(async () => {
    const ahead = 4;
    const x = r2(camBasis.px + camBasis.fx * ahead);
    const z = r2(camBasis.pz + camBasis.fz * ahead);
    const facing = r2(Math.atan2(-camBasis.fx, -camBasis.fz));
    const inRoom = !!interior;
    // Inside a room a portal is an EXIT: it has no model and its target is chosen
    // afterwards, because where you come out is usually another portal across the
    // city rather than a room. Outside, it is a door into the venue picked above.
    const model = inRoom ? null : assets.find((a) => a.slug === newModel) || null;
    const ref = inRoom ? null : (newRef || null);
    const saved = await createWorldObject({
      zone_id: inRoom ? interior : 'street',
      kind: 'portal',
      ...(model ? { assetId: model.assetId } : {}),
      ref,
      pos: [x, 0, z], rotY: facing, size: 6.7,
      data: {
        // Spans y −0.5 to 1.5 in the portal's own space, which contains the
        // street walking camera at EYE 0.75. Height is the trigger dimension
        // most likely to be wrong, so the default is the one that fires.
        trigger: { x: 0, y: 0.5, z: 0, w: 2, h: 2, d: 2 },
        target: inRoom ? null : (ref ? { kind: 'room', ref } : null),
      },
    });
    setSel(saved?.object_id ?? null); reload(); bump();
    say(inRoom ? 'placed — now pick where it comes out'
      : `door to ${ROOMS[ref]?.name || ref || '(nothing yet)'}${model ? '' : ' · no model'}`);
  });

  // VERIFY THE ROUND TRIP, don't assume it.
  //
  // A pit, a trigger box and a target all live in `data`, and an API that has not
  // been restarted since the column was wired up accepts the write, returns 200,
  // and stores none of it. Everything then behaves exactly as if you had never
  // pressed the button — which is indistinguishable from the feature being
  // broken, and sends you looking at the renderer for a bug in the transport.
  //
  // So: send it, read back what was stored, and say plainly when they disagree.
  const patch = (body) => run(async () => {
    if (!cur) return;
    const saved = await updateWorldObject(cur.object_id, body);
    if (body.data !== undefined && saved && !saved.data) {
      say('SAVED, BUT THE API DROPPED `data` — restart the API');
      console.warn('[portal] sent `data` and got nothing back. The world route needs restarting; until then pits, trigger boxes and targets cannot persist.', { sent: body.data, got: saved.data });
      return;
    }
    reload(); bump();
  });

  const nudge = (sx, sz, e) => {
    const s = stepFor('move', e);
    patch({ pos: [r2(cur.pos[0] + sx * s), cur.pos[1], r2(cur.pos[2] + sz * s)] });
  };
  const trig = { x: 0, y: 0.5, z: 0, w: 2, h: 2, d: 2, ...(cur?.data?.trigger || {}) };
  // Starts at roughly the entrance's own footprint (it measures 5.8 x 14.7m,
  // which is 2.6 x 6.7 world units) so the first press is already close instead
  // of a hole you have to grow from nothing. A legacy radius reads as a square.
  const rawPit = cur?.data?.pit;
  const pit = {
    w: rawPit?.w > 0 ? rawPit.w : (rawPit?.r > 0 ? rawPit.r * 2 : 2.6),
    d: rawPit?.d > 0 ? rawPit.d : (rawPit?.r > 0 ? rawPit.r * 2 : 6.7),
  };
  const setPit = (v) => patch({ data: { ...(cur.data || {}), pit: v } });
  const setTrig = (k, sign, e) => {
    const raw = r2(trig[k] + sign * stepFor('trig', e));
    const next = EXTENT_KEYS.has(k) ? Math.max(MIN_EXTENT, raw) : raw;
    patch({ data: { ...(cur.data || {}), trigger: { ...trig, [k]: next } } });
  };

  return (
    <div style={panel}>
      <AdminClose />
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7 }}>Portals</div>

      <div style={lab}>Asset</div>
      <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 5 }}>
        {asset ? `✓ ${asset.slug} (#${asset.assetId})` : 'not registered'}
      </div>
      {!asset && <button style={{ ...btn, width: '100%' }} disabled={busy} onClick={register}>Register subway entrance</button>}

      <div style={lab}>Placed ({rows.length})</div>
      {rows.map((o) => (
        <button
          key={o.object_id}
          style={{ ...btn, width: '100%', marginBottom: 4, fontSize: 11.5,
            background: sel === o.object_id ? '#7dd3ff' : 'rgba(125,211,255,0.14)',
            color: sel === o.object_id ? '#04121c' : '#7dd3ff' }}
          onClick={() => setSel(o.object_id)}
        >
          #{o.object_id} · {ROOMS[o.data?.target?.ref || o.ref]?.name || o.zone_id} @ {r2(o.pos[0])}, {r2(o.pos[2])}
        </button>
      ))}
      {auto.length > 0 && (
        <>
          <div style={lab}>Automatic venue doors ({auto.length})</div>
          <div style={{ fontSize: 10.5, opacity: 0.65, lineHeight: 1.35, marginBottom: 5 }}>
            Made from each voted-in venue&apos;s site. Customise saves it here at the same spot and size, ready to edit; delete it to go back to automatic.
          </div>
          {auto.map((d) => (
            <button
              key={d.object_id}
              style={{ ...btn, width: '100%', marginBottom: 4, fontSize: 11.5, background: 'rgba(125,211,255,0.06)', color: '#a9dcff', borderStyle: 'dashed' }}
              disabled={busy}
              onClick={() => customise(d)}
            >
              {ROOMS[d.ref]?.name || d.ref} · Customise
            </button>
          ))}
        </>
      )}
      {/* NEW DOOR — only outside. Inside a room the button makes an exit, whose
          target is picked after placing because it usually points at another
          portal rather than a room. */}
      {!interior && (
        <>
          <div style={lab}>New door opens onto</div>
          <select
            value={newRef}
            onChange={(e) => setNewRef(e.target.value)}
            style={{ width: '100%', background: 'rgba(0,0,0,0.4)', color: '#d9ecff', border: '1px solid rgba(125,211,255,0.4)', borderRadius: 8, padding: '7px 8px' }}
          >
            {doorless.length > 0 && (
              <optgroup label="No door yet — unreachable">
                {doorless.map((l) => <option key={l.interiorId} value={l.interiorId}>{l.name}</option>)}
              </optgroup>
            )}
            <optgroup label="Any room">
              {Object.keys(ROOMS).map((k) => <option key={k} value={k}>{ROOMS[k].name || k}</option>)}
            </optgroup>
          </select>
          {ROOMS[newRef]?.requires && (
            <div style={{ fontSize: 10.5, marginTop: 4, opacity: 0.85, color: '#ffd27d' }}>
              needs “{ROOMS[newRef].requires}” — this door will refuse anyone below that rung
            </div>
          )}

          <div style={lab}>Model</div>
          <select
            value={newModel}
            onChange={(e) => setNewModel(e.target.value)}
            style={{ width: '100%', background: 'rgba(0,0,0,0.4)', color: '#d9ecff', border: '1px solid rgba(125,211,255,0.4)', borderRadius: 8, padding: '7px 8px' }}
          >
            <option value="">None — the building has its own door</option>
            {assets.map((a) => <option key={a.assetId} value={a.slug}>{a.name || a.slug}</option>)}
          </select>

          <div style={{ fontSize: 10, opacity: 0.55, marginTop: 6, lineHeight: 1.45 }}>
            Stand back and LOOK AT the doorway, then place: it lands 4 units ahead
            of you, turned to face you. Standing in the doorway puts the door out
            in the street. With no model there is nothing to see except the
            wireframe trigger box, which is only drawn while this tool is open.
          </div>
        </>
      )}
      <button style={{ ...btn, width: '100%', marginTop: 4 }} disabled={busy} onClick={place}>
        {interior ? '+ Place exit ahead of me' : '+ Place door ahead of me'}
      </button>

      {cur && (
        <>
          {/* A ROOM or ANOTHER PORTAL. The second is what makes this transit:
              the platform's exit points at the station across the city, and you
              come out there. Targeting the station rather than a coordinate
              means moving that station moves every route into it. */}
          <div style={lab}>Opens onto</div>
          <select
            value={`${target.kind}:${target.ref ?? ''}`}
            onChange={(e) => {
              const [kind, ...rest] = e.target.value.split(':');
              const ref = rest.join(':');
              patch({
                ref: kind === 'room' ? ref : null,
                data: { ...(cur.data || {}), target: { kind, ref: kind === 'portal' ? Number(ref) : ref } },
              });
            }}
            style={{ width: '100%', background: 'rgba(0,0,0,0.4)', color: '#d9ecff', border: '1px solid rgba(125,211,255,0.4)', borderRadius: 8, padding: '7px 8px' }}
          >
            <optgroup label="A room">
              {Object.keys(ROOMS).map((k) => <option key={k} value={`room:${k}`}>{ROOMS[k].name || k}</option>)}
            </optgroup>
            <optgroup label="Another portal — surface there">
              {rows.filter((o) => o.object_id !== cur.object_id).map((o) => (
                <option key={o.object_id} value={`portal:${o.object_id}`}>
                  #{o.object_id} in {o.zone_id} @ {r2(o.pos[0])}, {r2(o.pos[2])}
                </option>
              ))}
            </optgroup>
          </select>

          {/* HOLD SHIFT. Said once, here, above every stepper it applies to —
              and the live step size is the label, so the modifier is discovered
              by pressing it rather than by reading about it. */}
          <div style={{
            ...lab, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            color: fineLabel ? '#9dff7d' : undefined, opacity: fineLabel ? 0.95 : 0.7,
          }}>
            <span>Move</span>
            <span style={{ letterSpacing: 0, textTransform: 'none', fontVariantNumeric: 'tabular-nums' }}>
              {fineLabel ? `fine · ${FINE.move}` : `⇧ for fine · ${STEP.move}`}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5 }}>
            <span /><button style={btn} onClick={(e) => nudge(0, -1, e)}>↑</button><span />
            <button style={btn} onClick={(e) => nudge(-1, 0, e)}>←</button>
            <div style={{ display: 'grid', placeItems: 'center', fontSize: 10, opacity: 0.6 }}>x/z</div>
            <button style={btn} onClick={(e) => nudge(1, 0, e)}>→</button>
            <span /><button style={btn} onClick={(e) => nudge(0, 1, e)}>↓</button><span />
          </div>

          <div style={lab}>Turn / size</div>
          <div style={{ display: 'flex', gap: 5 }}>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => patch({ rotY: r2((cur.rotY || 0) - stepFor('rot', e)) })}>⟲</button>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => patch({ rotY: r2((cur.rotY || 0) + stepFor('rot', e)) })}>⟳</button>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => patch({ size: r2(Math.max(1, (cur.size || 6.7) - stepFor('size', e))) })}>−</button>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => patch({ size: r2((cur.size || 6.7) + stepFor('size', e)) })}>+</button>
          </div>

          <div style={{
            ...lab, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            color: fineLabel ? '#9dff7d' : undefined, opacity: fineLabel ? 0.95 : 0.7,
          }}>
            <span>Trigger box</span>
            <span style={{ letterSpacing: 0, textTransform: 'none', fontVariantNumeric: 'tabular-nums' }}>
              {fineLabel ? `fine · ${FINE.trig}` : `⇧ for fine · ${STEP.trig}`}
            </span>
          </div>
          <div style={{ fontSize: 11, opacity: 0.8, fontVariantNumeric: 'tabular-nums', marginBottom: 4 }}>
            at {trig.x}, {trig.y}, {trig.z} · {trig.w}×{trig.h}×{trig.d}
          </div>
          {/* A doorway is thin. A 2-unit-deep box in front of one reaches out
              across the pavement and fires on anyone merely walking past, which
              reads as the building grabbing people. Depth is the dimension that
              wants shrinking, and at 0.05 a step it can reach the door itself. */}
          {trig.d >= 1 && (
            <div style={{ fontSize: 10, opacity: 0.8, color: '#ffd27d', marginBottom: 5, lineHeight: 1.4 }}>
              {trig.d} deep reaches {r2(trig.d / 2)} either side of the door — likely
              to catch people on the pavement.
            </div>
          )}
          <div style={{ display: 'flex', gap: 5 }}>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('z', -1, e)}>fwd −</button>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('z', 1, e)}>fwd +</button>
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('d', -1, e)}>depth −</button>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('d', 1, e)}>depth +</button>
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('w', -1, e)}>width −</button>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('w', 1, e)}>width +</button>
          </div>
          {/* HEIGHT, which was missing and is the one that actually stops a
              trigger firing. The box is tested against the CAMERA, and the
              camera is at eye height when you walk and far above it when you
              orbit — a 2-unit box centred near the ground is easy to step over
              in the literal sense. */}
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('h', -1, e)}>height −</button>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('h', 1, e)}>height +</button>
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('y', -1, e)}>lower</button>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('y', 1, e)}>raise</button>
          </div>
          {/* SIDEWAYS, which the box could not do at all. Everything else here
              moves it along the door's own depth or grows it symmetrically, so a
              door that is not centred on the model — and few are — could only be
              matched by widening the box until it covered the offset, which is
              precisely the over-wide trigger this panel now warns about. */}
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('x', -1, e)}>left</button>
            <button style={{ ...btn, flex: 1 }} onClick={(e) => setTrig('x', 1, e)}>right</button>
          </div>

          {/* The hole the station sinks into. Only meaningful on the street —
              a portal underground has nothing above it to open. */}
          {cur.zone_id === 'street' && (
            <>
              <div style={lab}>Sink into ground</div>
              <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4, fontVariantNumeric: 'tabular-nums' }}>
                {rawPit ? `${pit.w} wide × ${pit.d} deep · turns with the station` : `sitting on the surface — press to open ${pit.w} × ${pit.d}`}
              </div>
              <div style={{ display: 'flex', gap: 5 }}>
                <button style={{ ...btn, flex: 1 }} onClick={(e) => setPit({ w: Math.max(0.5, r2(pit.w - stepFor('pit', e))), d: pit.d })}>w −</button>
                <button style={{ ...btn, flex: 1 }} onClick={(e) => setPit({ w: r2(pit.w + stepFor('pit', e)), d: pit.d })}>w +</button>
              </div>
              <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
                <button style={{ ...btn, flex: 1 }} onClick={(e) => setPit({ w: pit.w, d: Math.max(0.5, r2(pit.d - stepFor('pit', e))) })}>d −</button>
                <button style={{ ...btn, flex: 1 }} onClick={(e) => setPit({ w: pit.w, d: r2(pit.d + stepFor('pit', e)) })}>d +</button>
              </div>
              <button style={{ ...btn, width: '100%', marginTop: 5 }} onClick={() => setPit(null)}>off</button>
            </>
          )}

          <button
            style={{ ...btn, width: '100%', marginTop: 10, borderColor: 'rgba(255,125,125,0.5)', color: '#ff9d9d' }}
            disabled={busy}
            onClick={() => run(async () => { await deleteWorldObject(cur.object_id); setSel(null); reload(); bump(); say('removed'); })}
          >
            Remove
          </button>
        </>
      )}

      {msg && <div style={{ fontSize: 10.5, marginTop: 8, opacity: 0.9, color: /fail|first/.test(msg) ? '#ff9d7d' : '#9dff7d' }}>{msg}</div>}
      <div style={{ fontSize: 10, opacity: 0.55, marginTop: 8, lineHeight: 1.45 }}>
        Walk out of a trigger before it can fire again — otherwise coming back up
        the stairs would send you straight back down.
      </div>
    </div>
  );
}
